<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-2">
      <h1 class="headline">Available Access Settings</h1>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" fab dark color="primary" @click="onCreateNew">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Create New Access Setting</span>
      </v-tooltip>
    </v-row>
    <v-row>
      <v-col class="pa-0">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="$apollo.loading"
          item-key="id"
          v-bind="pagingOptions"
          mobile-break-point="1264"
          class="elevation-1"
          fixed-header
          hide-default-footer
          :height="gridHeight"
          @update:options="onListOptionsUpdate"
          @current-items="dataTableReady"
        >
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              hideView
              @editItem="onEditItem"
              @deleteItem="onDeleteItem"
            >
            </default-list-actions>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <get-more-results
      :first="first"
      @limitChange="onLimitChange"
      :items="availableAccessSettings"
      :totalItems="totalItems"
      :loading="$apollo.loading"
      :loadMoreEnabled="loadMoreEnabled"
      @loadMore="loadMore('availableAccessSettings')"
    ></get-more-results>
    <available-access-setting-edit-dialog
      :show="showEditDialog"
      :value="editDialogItem"
      @close="showEditDialog = false"
      @input="onItemSave"
    ></available-access-setting-edit-dialog>
    <v-row v-if="debug">
      <v-col>
        <pre>{{ availableAccessSettings }}</pre>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { ListMixin } from "../../../../mixins/ListMixin";
import { removeGQLTypename } from "../../../../helpers";
import GetMoreResults from "../../shared/GetMoreResults.vue";
import DefaultListActions from "../../shared/DefaultListActions.vue";
import AvailableAccessSettingEditDialog from "./AvailableAccessSettingEditDialog.vue";

const LIST_QUERY = gql`
  query availableAccesSettings(
    $after: String
    $sort: SortOrderType
    $first: Int
  ) {
    availableAccessSettings(first: $first, orderBy: $sort, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          groupName
          settingName
          settingValue
          notes
        }
      }
    }
  }
`;
const CREATE_MUTATION = gql`
  mutation createAvailableAccessSetting($input: AvailableAccessSettingInput!) {
    createAvailableAccessSetting(input: $input) {
      id
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation updateAvailableAccessSetting($input: AvailableAccessSettingInput!) {
    updateAvailableAccessSetting(input: $input) {
      id
    }
  }
`;
const DELETE_MUTATION = gql`
  mutation deleteAvailableAccessSetting($id: ID!) {
    deleteAvailableAccessSetting(id: $id) {
      id
    }
  }
`;
export default {
  name: "available-access-settings",

  mixins: [ListMixin],

  components: {
    GetMoreResults,
    DefaultListActions,
    AvailableAccessSettingEditDialog,
  },

  data() {
    return {
      debug: false,
      headers: [
        { text: "Group Name", value: "groupName" },
        { text: "Setting Name", value: "settingName" },
        { text: "Default   Value", value: "settingValue" },
        { text: "Notes", value: "notes" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      showEditDialog: false,
      editDialogItem: null,
    };
  },

  computed: {
    items() {
      return this.availableAccessSettings && this.availableAccessSettings.edges
        ? this.availableAccessSettings.edges.map(removeGQLTypename)
        : [];
    },
  },

  methods: {
    onCreateNew() {
      this.editDialogItem = null;
      this.showEditDialog = true;
    },
    onEditItem(item) {
      //console.log("onEditItem", item);
      this.editDialogItem = { ...item };
      this.showEditDialog = true;
    },
    onDeleteItem(item) {
      console.log("onDeleteItem", item);
      this.$apollo
        .mutate({
          mutation: DELETE_MUTATION,
          variables: { id: item.id },
          refetchQueries: [
            {
              query: LIST_QUERY,
              variables: {
                after: this.after,
                first: this.first,
                sort: this.sort,
              },
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Available Access Setting Deleted.",
          });
        })
        .catch((error) => {
          console.error("Error deleting available access setting", error);
          this.$store.dispatch("showAppSnackbar", {
            msg: "Error deleting available access setting",
            color: "error",
          });
        });
    },
    onItemSave(item) {
      this.saveItem(item);
    },
    saveItem(item) {
      console.log("saveItem", item);
      const mutation = item.id ? UPDATE_MUTATION : CREATE_MUTATION;
      this.$apollo
        .mutate({
          mutation,
          variables: { input: item },
          refetchQueries: [
            {
              query: LIST_QUERY,
              variables: {
                after: this.after,
                first: this.first,
                sort: this.sort,
              },
            },
          ],
        })
        .then((result) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Available Access Setting Saved.",
          });
          this.showEditDialog = false;
          this.editDialogItem = null;
        })
        .catch((error) => {
          const errorMsg = error.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  apollo: {
    availableAccessSettings: {
      query: LIST_QUERY,
      variables() {
        return {
          after: this.after,
          first: this.first,
          filter: {
            ...this.searchParams,
          },
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data }) {
        this.totalItems = data.availableAccessSettings.totalCount;
      },
    },
  },
};
</script>

<style>
</style>


// WEBPACK FOOTER //
// src/components/gt/admin/available-access-settings/AvailableAccessSettings.vue